import { isValidPhoneFormat, normalizePhoneNumber } from '@markku/utils'
import { z } from 'zod'

/**
 * Grace period (in minutes) before billing a completed visit.
 * Invoices are sent shortly after visit completion.
 */
export const BILLING_GRACE_PERIOD_MINUTES = 30

/**
 * Payment period (in days) for invoices sent via email
 * Used in Stripe invoice creation and displayed to customers
 */
export const INVOICE_PAYMENT_PERIOD_DAYS = 14

// Reusable email schema: emails are stored lowercased and trimmed — auth
// looks users up by exact match on the lowercased login input, and
// users_email_unique_idx is on lower(email). Chain .nullable()/.optional()
// at the use site.
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Invalid email address')

// Reusable phone number schema with validation and normalization
export const phoneNumberSchema = z
  .string()
  .optional()
  .nullable()
  .transform((val) => normalizePhoneNumber(val))
  .refine(
    (val) => !val || isValidPhoneFormat(val),
    'Phone number must be in international format (e.g., +358405826303)',
  )

export const paginationSchema = z.object({
  total: z.number().int(), // Total number of items available
  page: z.number().int(), // The current page number
  pageSize: z.number().int(), // The number of items per page
  totalPages: z.number().int(), // The total number of pages
  hasNextPage: z.boolean(), // Indicator if a next page exists
})

export const listResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: z.array(dataSchema),
    pagination: paginationSchema,
  })

// Generic type for list responses
export type ListResponse<T> = {
  data: T[]
  pagination: {
    total: number
    page: number
    pageSize: number
    totalPages: number
    hasNextPage: boolean
  }
}

/**
 * Common React Query options used throughout the app.
 * Includes only the most frequently used options for simplicity and type inference.
 * This minimal set preserves return type inference while providing type safety.
 */
export type QueryOptions = {
  /** Whether the query should run. Useful for conditional queries. */
  enabled?: boolean
  /** Polling interval in milliseconds, or false to disable. */
  refetchInterval?: number | false
  /** Time in milliseconds before data is considered stale. */
  staleTime?: number
  /** Whether to refetch when the browser window regains focus. */
  refetchOnWindowFocus?: boolean
  /** Whether to refetch when the component mounts. */
  refetchOnMount?: boolean | 'always'
}
